import React, { useCallback, useEffect, useState } from "react"
import {
  Box,
  Button,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react"
import { usePortfolioCriteria, useSavePortfolioCriteria } from "../hooks/usePortfolioQueries"
import { useUserDetails } from "../hooks/useAuthQueries"
import MultiLocationPicker, { LocationEntry, newLocation } from "../components/MultiLocationPicker"

const BUILT_FORMS = ["Detached", "Semi-Detached", "Mid-Terrace", "End-Terrace"]
const EPC_BANDS = ["A", "B", "C", "D", "E", "F", "G"]

const emptyForm = {
  locations: [newLocation()] as LocationEntry[],
  built_forms: [...BUILT_FORMS],
  min_bedrooms: "",
  max_bedrooms: "",
  max_asking_price: "",
  min_epc_band: "",
}

const MyCriteria: React.FC = () => {
  const { data: userDetails } = useUserDetails()
  const activePortfolioId = userDetails?.data?.activePortfolioId
  const saveMutation = useSavePortfolioCriteria()

  const [form, setForm] = useState(emptyForm)
  const [saved, setSaved] = useState(false)

  const { data: criteriaResponse } = usePortfolioCriteria(activePortfolioId)

  useEffect(() => {
    setSaved(false)
  }, [activePortfolioId])

  useEffect(() => {
    if (criteriaResponse?.data && Object.keys(criteriaResponse.data).length > 0) {
      const c = criteriaResponse.data
      const apiLocations: LocationEntry[] = (c.locations ?? []).map((loc: any) => ({
        id: loc.id,
        location_name: loc.locationName ?? loc.location_name ?? "",
        location_lat: loc.locationLat != null ? Number(loc.locationLat) : (loc.location_lat != null ? Number(loc.location_lat) : null),
        location_lng: loc.locationLng != null ? Number(loc.locationLng) : (loc.location_lng != null ? Number(loc.location_lng) : null),
        radius_miles: loc.radiusMiles != null ? Number(loc.radiusMiles) : (loc.radius_miles != null ? Number(loc.radius_miles) : 2),
      }))
      setForm({
        locations: apiLocations.length > 0 ? apiLocations : [newLocation()],
        built_forms: c.builtForms ?? [],
        min_bedrooms: c.minBedrooms != null ? String(c.minBedrooms) : "",
        max_bedrooms: c.maxBedrooms != null ? String(c.maxBedrooms) : "",
        max_asking_price: c.maxAskingPrice != null ? String(c.maxAskingPrice) : "",
        min_epc_band: c.minEpcBand ?? "",
      })
    } else {
      setForm(emptyForm)
    }
  }, [criteriaResponse])

  const handleLocationsChange = useCallback((locations: LocationEntry[]) => {
    setForm((f) => ({ ...f, locations }))
  }, [])

  const toggleBuiltForm = (value: string) => {
    setForm((f) => ({
      ...f,
      built_forms: f.built_forms.includes(value)
        ? f.built_forms.filter((v) => v !== value)
        : [...f.built_forms, value],
    }))
  }

  const handleSave = async () => {
    if (!activePortfolioId) return
    const payload: any = {
      locations: form.locations
        .filter((l) => l.location_lat != null && l.location_lng != null)
        .map((l) => ({
          location_name: l.location_name,
          location_lat: l.location_lat,
          location_lng: l.location_lng,
          radius_miles: l.radius_miles,
        })),
      built_forms: form.built_forms,
      min_bedrooms: form.min_bedrooms !== "" ? Number(form.min_bedrooms) : null,
      max_bedrooms: form.max_bedrooms !== "" ? Number(form.max_bedrooms) : null,
      max_asking_price: form.max_asking_price !== "" ? form.max_asking_price : null,
      min_epc_band: form.min_epc_band,
    }
    await saveMutation.mutateAsync({ portfolioId: activePortfolioId, data: payload })
    setSaved(true)
  }

  return (
    <Box p={8}>
      <Heading size="xl" mb={2}>
        My Criteria
      </Heading>
      <Text color="gray.600" mb={6}>
        Define what properties you're looking for. Greenflip will source matching
        properties and notify you by email.
      </Text>
      <VStack gap={5} align="stretch" maxW="900px">

        {saved && (
          <Box
            bg="green.50"
            border="1px solid"
            borderColor="green.200"
            borderRadius="md"
            px={4}
            py={3}
          >
            <Text color="green.800" fontSize="sm">
              Your criteria has been saved. Greenflip will source matching
              properties and notify you by email.
            </Text>
          </Box>
        )}

        <Box>
          <Box h="1px" bg="gray.200" my={2} />
          <Text fontWeight="medium" mb={0}>
            Investment criteria
          </Text>
          <Text fontSize="sm" color="gray.500">
            Greenflip matches market listings against these criteria and alerts
            you to new opportunities.
          </Text>
        </Box>

        {/* Locations + map */}
        <Box>
          <Text fontWeight="medium" mb={3}>
            Target locations
          </Text>
          <MultiLocationPicker
            locations={form.locations}
            onChange={handleLocationsChange}
          />
        </Box>

        {/* Built form */}
        <Box>
          <Text fontWeight="medium" mb={2}>
            Property type
          </Text>
          <HStack gap={2} wrap="wrap">
            {BUILT_FORMS.map((bf) => {
              const selected = form.built_forms.includes(bf)
              return (
                <Box
                  key={bf}
                  as="button"
                  type="button"
                  onClick={() => toggleBuiltForm(bf)}
                  px={3}
                  py={1}
                  borderRadius="full"
                  border="1px solid"
                  borderColor={selected ? "green.600" : "gray.300"}
                  bg={selected ? "green.50" : "white"}
                  color={selected ? "green.800" : "gray.600"}
                  fontWeight={selected ? "semibold" : "normal"}
                  fontSize="sm"
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ borderColor: "green.400", color: "green.700" }}
                >
                  {bf}
                </Box>
              )
            })}
          </HStack>
        </Box>

        {/* Bedrooms + Max price */}
        <SimpleGrid columns={2} gap={6}>
          <Box>
            <Text fontWeight="medium" mb={2}>
              Bedrooms
            </Text>
            <HStack gap={2} align="center">
              <Input
                type="number"
                min={0}
                value={form.min_bedrooms}
                onChange={(e) => setForm((f) => ({ ...f, min_bedrooms: e.target.value }))}
                placeholder="Min"
                w="72px"
                size="sm"
                textAlign="center"
              />
              <Text color="gray.400" fontSize="xs">to</Text>
              <Input
                type="number"
                min={0}
                value={form.max_bedrooms}
                onChange={(e) => setForm((f) => ({ ...f, max_bedrooms: e.target.value }))}
                placeholder="Max"
                w="72px"
                size="sm"
                textAlign="center"
              />
            </HStack>
          </Box>

          <Box>
            <Text fontWeight="medium" mb={2}>
              Max asking price
            </Text>
            <HStack gap={0} maxW="180px">
              <Box
                px={3}
                h="36px"
                display="flex"
                alignItems="center"
                bg="gray.100"
                border="1px solid"
                borderColor="gray.200"
                borderRightWidth={0}
                borderLeftRadius="md"
                fontSize="sm"
                color="gray.500"
              >
                £
              </Box>
              <Input
                type="number"
                min={0}
                value={form.max_asking_price}
                onChange={(e) => setForm((f) => ({ ...f, max_asking_price: e.target.value }))}
                placeholder="Any"
                size="sm"
                borderLeftRadius={0}
              />
            </HStack>
          </Box>
        </SimpleGrid>

        {/* Min EPC band */}
        <Box>
          <Text fontWeight="medium" mb={2}>
            Minimum EPC rating
          </Text>
          <HStack gap={1.5} wrap="wrap">
            {["", ...EPC_BANDS].map((b) => {
              const selected = form.min_epc_band === b
              return (
                <Box
                  key={b || "any"}
                  as="button"
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, min_epc_band: b }))}
                  px={3}
                  py={1}
                  borderRadius="full"
                  border="1px solid"
                  borderColor={selected ? "green.600" : "gray.300"}
                  bg={selected ? "green.50" : "white"}
                  color={selected ? "green.800" : "gray.600"}
                  fontWeight={selected ? "semibold" : "normal"}
                  fontSize="sm"
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ borderColor: "green.400", color: "green.700" }}
                  minW="40px"
                  textAlign="center"
                >
                  {b || "Any"}
                </Box>
              )
            })}
          </HStack>
        </Box>

        <Box>
          <Button
            bg="green.800"
            color="white"
            _hover={{ bg: "green.900" }}
            size="lg"
            px={8}
            onClick={handleSave}
            loading={saveMutation.isPending}
            disabled={!activePortfolioId}
          >
            Save criteria
          </Button>
        </Box>
      </VStack>
    </Box>
  )
}

export default MyCriteria
